import { useEffect, useRef, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { ApiError } from "../../shared/api/client";
import { getEnv } from "../../shared/lib/env";
import {
  LiffLoginIncompleteError,
  LiffLoginRedirectError,
  getIdTokenSafe,
  getLiffRuntimeState,
  loginWithRedirect,
  rememberLiffLoginReturnTo
} from "../../shared/lib/liff";
import {
  clearFlowCounters,
  clearFlowTransientState,
  getFlowCounter,
  incrementFlowCounter,
  readFlowToken,
  rememberFlowReturnTo,
  rememberFlowTenantSlug,
  rememberFlowToken
} from "../../shared/session/flowStorage";
import { createPageErrorState, formatApiError, type PageErrorState } from "../../shared/session/flowSupport";
import { classifyMemberInviteFailure } from "../../shared/session/inviteFlowFailure";
import { canReuseExistingTenantSessionForEntry } from "../../shared/session/entryOwnerOnboarding";
import { useSessionBootstrap } from "../../shared/session/SessionBootstrapProvider";
import { PageScaffold } from "../../shared/ui/PageScaffold";

type MemberInvitePhase =
  | "idle"
  | "initializing-liff"
  | "requesting-line-login"
  | "establishing-session"
  | "consuming-member-invite"
  | "redirecting"
  | "error";

function buildMemberInviteUrl(tenantSlug: string, token: string): string {
  return `/t/${tenantSlug}/member-invite${token ? `?token=${encodeURIComponent(token)}` : ""}`;
}

export function MemberInvitePage() {
  const env = getEnv();
  const navigate = useNavigate();
  const { tenantSlug = "unknown-tenant" } = useParams();
  const { session, establishSessionWithLineToken, consumeMemberInviteToken } = useSessionBootstrap();
  const [searchParams] = useSearchParams();
  const [invitePhase, setInvitePhase] = useState<MemberInvitePhase>("idle");
  const [inviteErrorState, setInviteErrorState] = useState<PageErrorState>(createPageErrorState);
  const [retryNonce, setRetryNonce] = useState(0);
  const latestSessionRef = useRef(session);
  const bootstrapRunKeyRef = useRef<string | null>(null);
  const tokenFromQuery = searchParams.get("token")?.trim() ?? "";
  const hasTokenQueryParam = searchParams.has("token");
  const storedToken = readFlowToken("memberInvite", tenantSlug);
  const inviteToken = tokenFromQuery || storedToken || "";
  const tenantTopPath = `/t/${tenantSlug}`;
  const currentInviteUrl = buildMemberInviteUrl(tenantSlug, inviteToken);

  useEffect(() => {
    latestSessionRef.current = session;
  }, [session]);

  function handleReturnToTenantTop() {
    clearFlowTransientState("memberInvite");
    setInviteErrorState(createPageErrorState());
    bootstrapRunKeyRef.current = null;
    navigate(tenantTopPath, { replace: true });
  }

  function handleRetryLogin() {
    clearFlowCounters("memberInvite");
    setInviteErrorState(createPageErrorState());
    bootstrapRunKeyRef.current = null;
    setInvitePhase("idle");
    setRetryNonce((current) => current + 1);
  }

  useEffect(() => {
    const bootstrapRunKey = JSON.stringify({
      tenantSlug,
      inviteToken,
      tokenFromQuery,
      hasTokenQueryParam,
      retryNonce
    });

    if (bootstrapRunKeyRef.current === bootstrapRunKey) {
      return;
    }

    bootstrapRunKeyRef.current = bootstrapRunKey;
    let cancelled = false;

    async function runFlow() {
      if (hasTokenQueryParam && !tokenFromQuery) {
        setInvitePhase("error");
        setInviteErrorState({
          message: "member invite token が見つかりません。招待URL全体を開き直すか、新しい招待を利用してください。",
          debugMessage: null,
          allowRetry: false
        });
        clearFlowTransientState("memberInvite");
        return;
      }

      if (!inviteToken) {
        setInvitePhase("error");
        setInviteErrorState({
          message: "member invite token がありません。招待URL全体を開き直すか、新しい招待を利用してください。",
          debugMessage: null,
          allowRetry: false
        });
        return;
      }

      rememberFlowToken("memberInvite", inviteToken);
      rememberFlowTenantSlug("memberInvite", tenantSlug);
      rememberFlowReturnTo("memberInvite", currentInviteUrl);
      rememberLiffLoginReturnTo(currentInviteUrl);
      setInviteErrorState(createPageErrorState());
      setInvitePhase("initializing-liff");

      try {
        let nextSession = latestSessionRef.current;

        if (!canReuseExistingTenantSessionForEntry(tenantSlug, nextSession)) {
          const runtimeState = await getLiffRuntimeState(env.lineMiniAppLiffId);

          if (!runtimeState.isLoggedIn) {
            const loginAttemptCount = getFlowCounter("memberInvite", "loginAttemptCount");

            if (loginAttemptCount >= 1) {
              throw new LiffLoginIncompleteError();
            }

            incrementFlowCounter("memberInvite", "loginAttemptCount");
            setInvitePhase("requesting-line-login");
            await loginWithRedirect(env.lineMiniAppLiffId, currentInviteUrl);
            return;
          }

          const idToken = await getIdTokenSafe(env.lineMiniAppLiffId);

          if (!idToken) {
            const loginAttemptCount = getFlowCounter("memberInvite", "loginAttemptCount");

            if (loginAttemptCount >= 1) {
              throw new LiffLoginIncompleteError();
            }

            incrementFlowCounter("memberInvite", "loginAttemptCount");
            setInvitePhase("requesting-line-login");
            await loginWithRedirect(env.lineMiniAppLiffId, currentInviteUrl);
            return;
          }

          if (cancelled) {
            return;
          }

          setInvitePhase("establishing-session");
          nextSession = await establishSessionWithLineToken(tenantSlug, idToken);
        }

        if (cancelled) {
          return;
        }

        setInvitePhase("consuming-member-invite");
        nextSession = await consumeMemberInviteToken(tenantSlug, inviteToken);

        if (cancelled) {
          return;
        }

        clearFlowTransientState("memberInvite");
        setInvitePhase("redirecting");
        navigate(nextSession.entryPath, { replace: true });
      } catch (error) {
        if (cancelled) {
          return;
        }

        if (error instanceof LiffLoginRedirectError) {
          return;
        }

        if (error instanceof ApiError && error.code === "INVALID_LINE_TOKEN") {
          const backendRecoveryAttemptCount = getFlowCounter("memberInvite", "backendRecoveryAttemptCount");

          if (backendRecoveryAttemptCount < 1) {
            incrementFlowCounter("memberInvite", "backendRecoveryAttemptCount");
            setInvitePhase("requesting-line-login");
            await loginWithRedirect(env.lineMiniAppLiffId, currentInviteUrl);
            return;
          }
        }

        const isInviteExpired = error instanceof ApiError && error.code === "MEMBER_INVITE_EXPIRED";
        const isInviteRevoked = error instanceof ApiError && error.code === "MEMBER_INVITE_REVOKED";
        const isInviteAlreadyUsed = error instanceof ApiError && error.code === "MEMBER_INVITE_ALREADY_USED";
        const isInviteMissing = error instanceof ApiError && error.code === "MEMBER_INVITE_NOT_FOUND";

        if (isInviteExpired || isInviteRevoked || isInviteAlreadyUsed || isInviteMissing) {
          clearFlowTransientState("memberInvite");
        }

        const memberInviteFailure = classifyMemberInviteFailure(error);
        setInvitePhase("error");
        setInviteErrorState({
          message: memberInviteFailure.userMessage,
          debugMessage: formatApiError(error),
          allowRetry: memberInviteFailure.allowRetry
        });
      }
    }

    void runFlow();

    return () => {
      cancelled = true;
    };
  }, [
    consumeMemberInviteToken,
    currentInviteUrl,
    env.lineMiniAppLiffId,
    establishSessionWithLineToken,
    hasTokenQueryParam,
    inviteToken,
    navigate,
    retryNonce,
    tenantSlug,
    tokenFromQuery
  ]);

  return (
    <PageScaffold
      title="講師・管理者招待"
      description="講師 / 管理者向けの member invite リンクです。LIFF で LINE login を確認し、token consume 後に tenant 権限へ遷移します。"
      links={[{ to: "/t/:tenantSlug/entry", label: "利用開始へ戻る" }]}
    >
      {invitePhase === "initializing-liff" ? <p>LIFFを初期化しています。</p> : null}
      {invitePhase === "requesting-line-login" ? <p>LINEログインを確認しています。</p> : null}
      {invitePhase === "establishing-session" ? <p>セッションを作成しています。</p> : null}
      {invitePhase === "consuming-member-invite" ? <p>招待された権限を設定しています。</p> : null}
      {invitePhase === "redirecting" ? <p>権限に応じた画面へ移動しています。</p> : null}
      {invitePhase === "error" ? (
        <div style={{ display: "grid", gap: 8 }}>
          <p style={{ margin: 0 }}>
            {inviteErrorState.message ?? "ログインに失敗しました。ホーム画面に戻り、もう一度お試しください。"}
          </p>
          <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
            <button type="button" onClick={handleReturnToTenantTop}>
              ホーム画面に戻る
            </button>
            {inviteErrorState.allowRetry ? (
              <button type="button" onClick={handleRetryLogin}>
                もう一度ログインする
              </button>
            ) : null}
          </div>
          {env.reviewDebugEnabled && inviteErrorState.debugMessage ? (
            <pre
              style={{
                margin: 0,
                padding: 12,
                borderRadius: 8,
                background: "#f6f6f6",
                overflowX: "auto",
                whiteSpace: "pre-wrap",
                wordBreak: "break-word"
              }}
            >
              {inviteErrorState.debugMessage}
            </pre>
          ) : null}
        </div>
      ) : null}
    </PageScaffold>
  );
}
